import React, { useEffect, useState } from 'react';
import api from '../../../../../../api';
import {
  formatPlaidDate,
  getPlaidConnections,
  maskPlaidAccount,
} from './plaidIdentity';

const Field = ({ label, children, wide = true }) => (
  <div
    className={`verification-field${wide ? ' verification-field-wide' : ''}`}
  >
    <div className="verification-field-label">{label}</div>
    <div className="verification-field-value">
      {children || 'Not available'}
    </div>
  </div>
);

const Connection = ({ connection, initiallyOpen }) => {
  const [open, setOpen] = useState(initiallyOpen);

  return (
    <details
      className="verification-accordion"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="verification-accordion-summary">
        <span>
          <strong>{connection.institutionName.toUpperCase()}</strong>
          <span className="verification-subtitle">
            {formatPlaidDate(connection.updated)}
          </span>
        </span>
        <span
          className="verification-badge verification-badge-success"
          title="Saved Plaid connection. Live connection status is unavailable."
        >
          Saved
        </span>
      </summary>
      <div className="verification-fields">
        <Field label="Last updated">
          {formatPlaidDate(connection.updated)}
        </Field>
        <Field label="Plaid Name">{connection.names}</Field>
        {(connection.addresses.length ? connection.addresses : [{}]).map(
          (address, index) => (
            <React.Fragment key={index}>
              <Field label="Identity Address">{address.street}</Field>
              <Field label="Identity City">{address.city}</Field>
              <Field label="Identity State" wide={false}>
                {address.state}
              </Field>
              <Field label="Identity Zip" wide={false}>
                {address.zip_code}
              </Field>
            </React.Fragment>
          )
        )}
        <Field label="Phone">
          {connection.phone_numbers.length
            ? connection.phone_numbers.map((phone, index) => (
                <div key={index}>{phone.phone_number || 'Not available'}</div>
              ))
            : null}
        </Field>
        <Field label="Email">
          {connection.emails.length
            ? connection.emails.map((email, index) => (
                <div key={index}>{email.email_id || 'Not available'}</div>
              ))
            : null}
        </Field>
        {connection.accounts.length ? (
          connection.accounts.map((account, index) => (
            <React.Fragment key={account.id || index}>
              {connection.accounts.length > 1 && (
                <div className="verification-field verification-field-wide">
                  <strong>Account {index + 1}</strong>
                  <span className="verification-subtitle">
                    {[account.type, account.method].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
              <Field label="Account">
                {maskPlaidAccount(account.account_number_redacted)}
              </Field>
              <Field label="Routing Number">{account.routing_number}</Field>
              <Field label="Bank Name">{account.bank_name}</Field>
            </React.Fragment>
          ))
        ) : (
          <div className="verification-empty verification-field-wide">
            No active accounts available for this connection.
          </div>
        )}
      </div>
    </details>
  );
};

export default function PlaidIdentityPanel({ paymentProfile, verification }) {
  const userId = paymentProfile?.user;
  const profileId = paymentProfile?.id;
  const requestKey = `${profileId || ''}:${userId || ''}`;
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState({});

  useEffect(() => {
    let active = true;
    setResult({ requestKey, loading: Boolean(userId) });

    if (userId) {
      api.plaid
        .getUserPlaid(userId)
        .then((items) => {
          if (active) {
            setResult({ requestKey, connections: getPlaidConnections(items) });
          }
        })
        .catch(() => {
          if (active) {
            setResult({ requestKey, error: true });
          }
        });
    }

    return () => {
      active = false;
    };
  }, [userId, requestKey, retry]);

  const currentResult = result.requestKey === requestKey ? result : {};
  const connections = currentResult.connections || [];
  const loading =
    Boolean(userId) &&
    (currentResult.loading || currentResult.requestKey !== requestKey);

  return (
    <section
      className="verification-panel"
      aria-label="Plaid Identity"
      aria-busy={loading}
    >
      <h2 className="verification-panel-title">Plaid Identity</h2>
      <p className="verification-subtitle">
        {loading
          ? 'Loading connections…'
          : currentResult.error
          ? 'Connection data unavailable'
          : `${connections.length} ${
              connections.length === 1 ? 'connection' : 'connections'
            }${connections.length ? ' · newest expanded' : ''}`}
      </p>
      {!userId ? (
        <p className="verification-empty">
          Plaid information is unavailable until a user is linked to this
          payment profile.
        </p>
      ) : loading ? (
        <p className="verification-empty" role="status">
          Loading Plaid information…
        </p>
      ) : currentResult.error ? (
        <div className="verification-error" role="alert">
          <p>Unable to load Plaid information.</p>
          <button
            type="button"
            className="verification-button"
            onClick={() => setRetry((value) => value + 1)}
          >
            Try again
          </button>
        </div>
      ) : connections.length ? (
        connections.map((connection, index) => (
          <Connection
            key={`${requestKey}:${connection.key}`}
            connection={connection}
            initiallyOpen={index === 0}
          />
        ))
      ) : (
        <p className="verification-empty">
          {verification?.account_entry_method === 'manually'
            ? 'No Plaid connections found. The current payment account was entered manually.'
            : 'No Plaid connections found.'}
        </p>
      )}
      <p className="verification-subtitle">
        Identity information is provided by Plaid. Last updated reflects the
        saved record.
      </p>
    </section>
  );
}
